import { useCallback, useMemo, useState } from 'react';
import { getIssueTypesAndFields } from '../../services/index.tsx';
import type {
	IssueTypeFieldsUtil,
	IssueTypeToFieldsMap,
	UseLazyIssueTypeFieldsArguments,
} from '../../services/types.tsx';

/**
 * This hook is responsible for fetching the issue types and its associated fields for a given project key.
 * It exposes a function to load the data on demand (lazily) and a function to check if a specific issue type has a specific field.
 * */
export const useLazyIssueTypesFieldsMap = ({
	projectKey,
}: UseLazyIssueTypeFieldsArguments): IssueTypeFieldsUtil => {
	// This state is kept per individual ICC trigger (each footer "+ Create" and sibling "+" (hidden) button)
	// Use shared state instead of local state for "dirty" to avoid re-fetching between cards
	const [dirty, setDirty] = useState(false);
	const [issueTypeMap, setIssueTypeMap] = useState<IssueTypeToFieldsMap | null>(null);
	const [error, setError] = useState<Error | null>(null);
	const loading = dirty && !issueTypeMap && !error;
	const done = dirty && !loading;

	const load = useCallback(() => {
		// define caching strategy here. For now there's none and it won't be called twice for each button
		if (dirty || !projectKey) return;
		setDirty(true);
		getIssueTypesAndFields({ projectKey }).then(setIssueTypeMap).catch(setError);
	}, [dirty, projectKey]);

	const checkIssueTypeHasField = useCallback(
		(issueTypeId: string | number | null, fieldId: string): boolean => {
			if (!issueTypeId || !issueTypeMap || !issueTypeMap[issueTypeId]) return false;
			return issueTypeMap[issueTypeId].fields.some((f) => f.fieldId === fieldId);
		},
		[issueTypeMap],
	);

	return useMemo(
		() => ({
			load,
			error,
			done,
			loading,
			issueTypeMap,
			checkIssueTypeHasField,
		}),
		[load, error, done, loading, issueTypeMap, checkIssueTypeHasField],
	);
};
